import {v4 as uuidv4} from 'uuid';
import {SessionSearch} from '../../components/session-search/types/SessionSearch';
import {
  Query,
  QueryType,
  QueryOperator,
} from '../../components/session-search/types/Query';
import {
  Criteria,
  CriteriaType,
} from '../../components/session-search/types/Criteria';
import {
  SessionSearchItem,
  AdvancedSearchTypeEnum,
  SavedSearchStatus,
} from '../../../src/api/types/sessionSearch.type';

const isOperatorToken = (value: string): boolean => {
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  return (
    upper === 'AND' ||
    upper === 'OR' ||
    upper === 'NOT' ||
    trimmed === '(' ||
    trimmed === ')'
  );
};

const parseBacktickSeparatedCriteria = (decodedCriteria: string): Criteria[] =>
  decodedCriteria
    .split('`')
    .filter((part) => part.trim() !== '')
    .map((part) => ({
      id: uuidv4(),
      type: (isOperatorToken(part) ? 'Operator' : 'Operand') as CriteriaType,
      value: isOperatorToken(part) ? part.trim().toUpperCase() : part,
    }));

const parseOperatorSeparatedCriteria = (decodedCriteria: string): Criteria[] =>
  decodedCriteria
    .split(/(\bAND\b|\bOR\b|\bNOT\b)/g)
    .filter((part) => part.trim() !== '')
    .map((part) => ({
      id: uuidv4(),
      type: (isOperatorToken(part) ? 'Operator' : 'Operand') as CriteriaType,
      value: isOperatorToken(part) ? part.trim().toUpperCase() : part.trim(),
    }));

const parseDelimitedCriteria = (decodedCriteria: string): Criteria[] =>
  decodedCriteria.includes('`')
    ? parseBacktickSeparatedCriteria(decodedCriteria)
    : parseOperatorSeparatedCriteria(decodedCriteria);

const parseCriteriaDB = (
  criteriaDB: string,
  advType: AdvancedSearchTypeEnum
): Criteria[] => {
  if (!criteriaDB || criteriaDB.trim() === '') {
    return [];
  }

  const decodedCriteria = criteriaDB
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();

  const criteria: Criteria[] = [];

  switch (advType) {
    case 'CONTENTMETADATA': {
      const fieldPattern =
        /([A-Za-z0-9_]{1,100}):[ \t\r\n]{0,20}\(([^)\r\n]{1,500})\)/g;
      let match;

      while ((match = fieldPattern.exec(decodedCriteria)) !== null) {
        const fieldName = match[1];
        const fieldValue = match[2].replace(/["']/g, '');

        const displayText = `${fieldName}: ${fieldValue}`;
        criteria.push({
          id: uuidv4(),
          type: 'Operand' as CriteriaType,
          value: displayText,
        });
      }

      if (criteria.length === 0) {
        if (decodedCriteria.includes('`') || /\b(AND|OR|NOT)\b/.test(decodedCriteria)) {
          criteria.push(...parseDelimitedCriteria(decodedCriteria));
        } else {
          criteria.push({
            id: uuidv4(),
            type: 'Operand' as CriteriaType,
            value: decodedCriteria,
          });
        }
      }
      break;
    }

    case 'CONCEPTUAL':
      criteria.push({
        id: uuidv4(),
        type: 'Operand' as CriteriaType,
        value: decodedCriteria,
      });
      break;

    case 'WORKPRODUCT':
    case 'ANALYTICS':
      criteria.push(...parseDelimitedCriteria(decodedCriteria));
      break;

    default:
      criteria.push({
        id: uuidv4(),
        type: 'Operand' as CriteriaType,
        value: decodedCriteria,
      });
  }

  return criteria;
};

const mapAdvancedSearchTypeToQueryType = (
  advType: AdvancedSearchTypeEnum
): QueryType => {
  switch (advType) {
    case 'CONTENTMETADATA':
      return QueryType.ContentAndMetadata;
    case 'CONCEPTUAL':
      return QueryType.Conceptual;
    case 'WORKPRODUCT':
      return QueryType.WorkProduct;
    case 'ANALYTICS':
      return QueryType.Analytics;
    default:
      return QueryType.ContentAndMetadata;
  }
};

const mapOperatorToQueryOperator = (operator: string | null): QueryOperator => {
  if (!operator) return 'AND';

  const upperOperator = operator.toUpperCase();
  if (
    upperOperator === 'AND' ||
    upperOperator === 'OR' ||
    upperOperator === 'NOT'
  ) {
    return upperOperator as QueryOperator;
  }

  return 'AND';
};

export const transformSavedSearchToSessionSearch = (
  sessionSearchItem: SessionSearchItem
): SessionSearch => {
  const queries: Query[] = [];

  if (
    sessionSearchItem.advancedSearchTypes &&
    sessionSearchItem.advancedSearchTypes.length > 0
  ) {
    const sortedTypes = [...sessionSearchItem.advancedSearchTypes].sort(
      (a, b) => a.sequenceNo - b.sequenceNo
    );

    for (const advType of sortedTypes) {
      const queryType = mapAdvancedSearchTypeToQueryType(
        advType.advancedSearchType
      );

      const criteria = parseCriteriaDB(
        advType.criteriaDB,
        advType.advancedSearchType
      );

      const query: Query = {
        type: queryType,
        value: criteria,
        operator: mapOperatorToQueryOperator(advType.operator),
      };

      queries.push(query);
    }
  }

  if (queries.length === 0) {
    queries.push({
      type: QueryType.ContentAndMetadata,
      value: [],
      operator: 'AND',
    });
  }

  const sessionSearch: SessionSearch = {
    id: uuidv4(),
    name: sessionSearchItem.searchTitle || sessionSearchItem.searchLabel || '',
    familyMemberDocuments:
      sessionSearchItem.advancedSearchOptions?.includeFamilyMember || false,
    threadedEmailDocuments:
      sessionSearchItem.advancedSearchOptions?.includeEmailDocuments || false,
    nearDuplicateDocuments:
      sessionSearchItem.advancedSearchOptions?.includeNearDupe || false,
    regexOnWorkProductAttributes: false,
    isSelected: true,
    isPinned: sessionSearchItem.isPinned || false,
    isSaved: true,
    lastRuntime: undefined,
    advancedSearchId:
      sessionSearchItem.searchStatus === SavedSearchStatus.COMPLETED ||
      sessionSearchItem.searchStatus === SavedSearchStatus.DRAFT
        ? sessionSearchItem.searchID
        : undefined,
    query: queries,
  };

  return sessionSearch;
};
